// context/TopBarContext.tsx

import React, { createContext, useContext, useState, useRef } from "react";

interface NavState {
  path: string;
  title: string;
  showBackButton: boolean;
}

interface TopBarContextType {
  title: string;
  showBackButton: boolean;
  setTitle: (title: string) => void;
  setShowBackButton: (value: boolean) => void;
  pushNavState: (state: NavState) => void;
  popNavState: () => NavState | null;
}

const TopBarContext = createContext<TopBarContextType | undefined>(undefined);

export const TopBarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [title, setTitle] = useState("");
  const [showBackButton, setShowBackButton] = useState(false);
  const historyStack = useRef<NavState[]>([]);

  const pushNavState = (state: NavState) => {
    historyStack.current.push(state);
  };

  const popNavState = (): NavState | null => {
    if (historyStack.current.length > 1) {
      historyStack.current.pop(); // Remove current screen
      return historyStack.current[historyStack.current.length - 1]; // Return previous
    }
    return null;
  };

  return (
    <TopBarContext.Provider
      value={{
        title,
        showBackButton,
        setTitle,
        setShowBackButton,
        pushNavState,
        popNavState,
      }}
    >
      {children}
    </TopBarContext.Provider>
  );
};

export const useTopBar = () => {
  const context = useContext(TopBarContext);
  if (!context)
    throw new Error("useTopBar must be used within a TopBarProvider");
  return context;
};
